import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import {
  emojiData,
  type EmojiCategory,
  type EmojiEntry,
} from "@cohesive/ui-components";
import { $getSelection, $isRangeSelection, type LexicalEditor } from "lexical";
import { Smile } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { canvasToolbarButtonVariants } from "../../components/toolbar/canvasToolbarStyles";

const EMOJI_CATEGORY_ORDER = [
  "Smileys & Emotion",
  "People & Body",
  "Animals & Nature",
  "Food & Drink",
  "Activities",
  "Travel & Places",
  "Objects",
  "Symbols",
  "Flags",
];

interface CanvasEmojiPickerProps {
  editor: LexicalEditor | null;
  onPopoverOpenChange?: (isOpen: boolean) => void;
}

export function CanvasEmojiPicker({
  editor,
  onPopoverOpenChange,
}: CanvasEmojiPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onPopoverOpenChange?.(next);
    if (next) {
      setSearch("");
      setDebouncedSearch("");
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 150);
  };

  const categories = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return EMOJI_CATEGORY_ORDER.flatMap((key) => {
      const cat: EmojiCategory | undefined = emojiData[key];
      if (!cat) return [];
      const emojis = cat.emojis.filter(
        (e: EmojiEntry) =>
          Number(e.unicode_version) < 15 &&
          (!q ||
            e.name.includes(q) ||
            e.keywords.some((k: string) => k.includes(q))),
      );
      if (!emojis.length) return [];
      return [{ name: cat.name, emojis }];
    });
  }, [debouncedSearch]);

  const handleSelect = (emoji: string) => {
    if (!editor) return;
    editor.update(() => {
      const sel = $getSelection();
      if ($isRangeSelection(sel)) sel.insertText(emoji);
    });
    handleOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              onClick={(e) => e.stopPropagation()}
              className={cn(canvasToolbarButtonVariants({ active: open }))}
            >
              <Smile className="size-4" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">Emoji</TooltipContent>
      </Tooltip>

      <PopoverContent
        side="top"
        align="center"
        sideOffset={8}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        className="w-72 p-0 bg-neutral-0/95 backdrop-blur-lg border rounded-2xl shadow-2xl shadow-black/20 overflow-hidden"
      >
        {/* Search */}
        <div className="flex items-center gap-2 border-b px-3">
          <input
            ref={searchRef}
            type="text"
            placeholder="Search emoji…"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className="flex h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground/60"
          />
        </div>

        {/* Grid */}
        <div
          className="h-60 overflow-y-auto overscroll-contain p-2 space-y-2"
          onWheel={(e) => e.stopPropagation()}
        >
          {categories.length === 0 ? (
            <p className="text-xs text-center text-muted-foreground py-8">
              No emoji found
            </p>
          ) : (
            categories.map((cat) => (
              <div key={cat.name}>
                {!debouncedSearch && (
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/60 px-0.5 mb-1">
                    {cat.name}
                  </p>
                )}
                <div className="grid grid-cols-9 gap-0.5">
                  {cat.emojis.map((entry: EmojiEntry) => (
                    <button
                      key={entry.slug}
                      title={entry.name}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(entry.emoji);
                      }}
                      className="h-7 w-7 flex items-center justify-center text-base rounded-lg hover:bg-n-95/8 dark:hover:bg-n-95/10 transition-colors focus:outline-none leading-none"
                    >
                      {entry.emoji}
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
